import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Field from "components/Field";
import SelectCorporateEntity from "components/SelectCorporateEntity";
import SelectProduct from "components/SelectProduct";
import SelectProductVersion from "components/SelectProductVersion";
import SelectProductYear from "components/SelectProductYear";
import SelectSkuAvailability from "components/SelectSkuAvailability";
import SelectSkuFormat from "components/SelectSkuFormat";
import SelectSkuStatus from "components/SelectSkuStatus";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { pick } from "lodash";
import SelectProductType from "pages/EvaluationsPage/SelectProductType";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ISkuContentRuleProduct,
  SkuContentRuleProductRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";

const useStyles = makeStyles({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  paper: {
    marginTop: 10,
  },
});

type SkuRuleProductFormProps = {
  skuId: string;
  skuRuleId: string;
  skuContentRuleProduct?: ISkuContentRuleProduct | null | undefined;
  dialogTitleKey: string;
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
};

type SkuRuleProductFormValues = {
  id: string | undefined;
  productTypeId: string | undefined;
  productId: string | undefined;
  productYearId: string | undefined;
  productVersionId: string | undefined;
  skuFormatId: string | undefined;
  skuAvailabilityId: string | undefined;
  corporateEntityId: string | undefined;
  skuStatusId: string | undefined;
  minPrice: number | undefined;
  maxPrice: number | undefined;
};

const SkuRuleDetailProductForm = ({
  skuId,
  skuRuleId,
  skuContentRuleProduct,
  dialogTitleKey,
  onClose,
  onCreate,
  onUpdate,
}: SkuRuleProductFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const { organizationId } = contextStore.useState();

  const init = useMemo(
    () => ({
      skuRuleId,
      ...pick(skuContentRuleProduct ?? {}, [
        "id",
        "productTypeId",
        "productId",
        "productYearId",
        "productVersionId",
        "skuFormatId",
        "skuAvailabilityId",
        "corporateEntityId",
        "skuStatusId",
        "minPrice",
        "maxPrice",
      ]),
    }),
    [skuContentRuleProduct, skuRuleId]
  );

  const form = useMemo(() => {
    const onSubmit = async (values: SkuRuleProductFormValues) => {
      try {
        const response = await registerPromise(
          skuService.saveSkuContentRuleProduct(
            skuId,
            skuRuleId,
            SkuContentRuleProductRequest.fromJS(values)
          )
        );

        queryClient.invalidateQueries(["skuRuleDetails", skuId]);

        if (values.id === null) {
          onCreate();
        } else {
          onUpdate();
        }

        onClose();

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle id="form-dialog-title">
                <T id={dialogTitleKey} />
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={4}>
                  <Field name="productId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProduct
                            label={t({
                              id: "productLabel",
                            })}
                            organizationId={organizationId}
                            searchByName={true}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productTypeId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductType
                            label={t({
                              id: "typeLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="skuFormatId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSkuFormat
                            label={t({
                              id: "skuFormatLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="skuAvailabilityId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSkuAvailability
                            label={t({
                              id: "skuAvailabilityLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="skuStatusId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSkuStatus
                            {...input}
                            label={t({
                              id: "skuStatusLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productVersionId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductVersion
                            {...input}
                            label={t({
                              id: "productVersionLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productYearId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductYear
                            label={t({
                              id: "productYearLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="corporateEntityId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectCorporateEntity
                            label={t({
                              id: "corporateEntityLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="minPrice">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <TextField
                          variant="standard"
                          fullWidth
                          type="number"
                          label={t({ id: "minPriceLabel" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          inputProps={{
                            min: 0,
                            step: ".01",
                          }}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="maxPrice">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <TextField
                          variant="standard"
                          fullWidth
                          type="number"
                          label={t({ id: "maxPriceLabel" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          inputProps={{
                            min: 0,
                            step: ".01",
                          }}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                {submitting && <CircularProgress size={16} />}
                <Button disabled={submitting} onClick={onClose}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button type="submit" color="primary" disabled={submitting}>
                  {t({ id: "saveFilterLabel" })}
                </Button>
              </DialogActions>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    );
  }, [
    organizationId,
    styles.feedback,
    dialogTitleKey,
    init,
    onClose,
    onCreate,
    onUpdate,
    queryClient,
    registerPromise,
    skuId,
    skuRuleId,
    skuService,
    snackbarMessage,
    t,
  ]);

  return form;
};

export default SkuRuleDetailProductForm;
